import { IconAlertCircle, IconCheck } from '@tabler/icons-react';

import {
    Accordion,
    Alert,
    Button,
    Flex,
    Text,
    ThemeIcon,
    Title
} from '@mantine/core';

import { usePostApiAdminSettingsMigrate } from '../../api/admin-settings/admin-settings';
import { AdminSettingsDto } from '../../api/models';

interface MigrationsProps {
    adminSettings: AdminSettingsDto;
    refetch: () => void;
}

export const Migrations = ({ adminSettings, refetch }: MigrationsProps) => {
    const { mutateAsync: mutateMigrationAsync, isPending: isMigrationLoading } =
        usePostApiAdminSettingsMigrate();

    const handleMigration = async () => {
        await mutateMigrationAsync();
        refetch();
    };

    const pendingMigrations = adminSettings.dbReport.pendingMigrations.map(
        (m: string) => (
            <Flex key={m} gap={'md'} align={'center'}>
                <ThemeIcon color={'green'}>
                    <IconCheck />
                </ThemeIcon>
                <Text c={'red'}>{m}</Text>
            </Flex>
        )
    );
    const appliedMigrations = adminSettings.dbReport.appliedMigrations.map(
        (m: string) => (
            <Flex key={m} gap={'md'} align={'center'}>
                <ThemeIcon color={'green'}>
                    <IconCheck />
                </ThemeIcon>
                <Text c={'green'}>{m}</Text>
            </Flex>
        )
    );
    const isPendingMigrations =
        adminSettings.dbReport.pendingMigrations.length > 0;
    const isAppliedMigrations =
        adminSettings.dbReport.appliedMigrations.length > 0;

    return (
        <>
            <Text>Update the database to the latest version.</Text>
            {!isPendingMigrations && (
                <Text>There are no pending migrations.</Text>
            )}
            {isPendingMigrations && (
                <Flex direction={'column'} gap={'md'}>
                    <Alert
                        icon={<IconAlertCircle size='md' />}
                        title='Migrations Pending'
                        color='red'
                    >
                        <Text>
                            The database is{' '}
                            {adminSettings.dbReport.pendingMigrations.length}{' '}
                            migrations behind the current version.
                        </Text>
                    </Alert>
                    {isPendingMigrations && (
                        <Button
                            onClick={handleMigration}
                            loading={isMigrationLoading}
                        >
                            Apply Migrations
                        </Button>
                    )}
                </Flex>
            )}

            <Accordion
                w={'full'}
                defaultValue={
                    isPendingMigrations ? 'db-migrations-pending' : null
                }
            >
                {isPendingMigrations && (
                    <Accordion.Item value='db-migrations-pending'>
                        <Accordion.Control>
                            <Title order={4}>Pending Migrations</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Flex direction={'column'} gap={'0.5rem'}>
                                {pendingMigrations}
                            </Flex>
                        </Accordion.Panel>
                    </Accordion.Item>
                )}
                {isAppliedMigrations && (
                    <Accordion.Item value='db-migrations-applied'>
                        <Accordion.Control>
                            <Title order={4}>Applied Migrations</Title>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Flex direction={'column'} gap={'0.5rem'}>
                                {appliedMigrations}
                            </Flex>
                        </Accordion.Panel>
                    </Accordion.Item>
                )}
            </Accordion>
        </>
    );
};
